import styles from 'components/search/search-columns/source-column/source-column-filter.module.css'

import { LuFilter } from '@react-icons/all-files/lu/LuFilter'
import { RxInfoCircled } from '@react-icons/all-files/rx/RxInfoCircled'
import React, { useCallback, useMemo } from 'react'

import {
  selectSearchPage,
  setFilters,
} from '@disco/state-management/reducers/search-page'
import {
  SOURCE_FILTER_DEFINITION,
  extractSourceFilterFromFilters,
} from '@disco/utils/search-page'

import type { FilterState } from 'types/filter-page'
import type { SourceScope } from 'types/search-page'

import {
  Checkbox,
  DropdownMenu,
  Flex,
  IconButton,
  Text,
  Tooltip,
} from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

const MDS_SUPPORT_URL =
  'https://support.disco.ac/home/multidisco-search-feature-guide-for-music-supervisors'

const SOURCE_OPTIONS: {
  label: string
  value: SourceScope
  tooltip?: string
}[] = [
  { label: 'My DISCO', value: 'business' },
  {
    label: 'MultiDISCO Search',
    value: 'mds',
    tooltip: 'What is MultiDISCO Search?',
  },
]

const ALL_SCOPES: SourceScope[] = SOURCE_OPTIONS.map(o => o.value)

const replaceSourceFilter = (
  filters: FilterState[],
  values: SourceScope[],
): FilterState[] => {
  const others = filters.filter(
    f => f.definition.field !== SOURCE_FILTER_DEFINITION.field,
  )

  if (ALL_SCOPES.every(scope => values.includes(scope))) {
    return others
  }

  return [
    ...others,
    {
      definition: SOURCE_FILTER_DEFINITION,
      operator: 'equals',
      value: values,
      hidden: true,
    },
  ]
}

export const SourceColumnFilter = () => {
  const dispatch = useDispatch()
  const { filters } = useSelector(selectSearchPage)

  const selectedScopes = useMemo<SourceScope[]>(() => {
    const sourceFilter = extractSourceFilterFromFilters(filters)
    return sourceFilter.kind === 'scope' ? sourceFilter.values : ALL_SCOPES
  }, [filters])

  const handleToggle = useCallback(
    (value: SourceScope) => {
      const newScopes = selectedScopes.includes(value)
        ? selectedScopes.filter(s => s !== value)
        : [...selectedScopes, value]

      // Defensive fallback: the dropdown disables the last checked item so
      // this branch is unreachable from the UI, but guards against future
      // changes (keyboard shortcuts, programmatic toggles, etc.).
      if (newScopes.length === 0) return

      dispatch(setFilters(replaceSourceFilter(filters, newScopes)))
    },
    [dispatch, filters, selectedScopes],
  )

  const isFiltered = selectedScopes.length < SOURCE_OPTIONS.length

  return (
    <Flex align="center" gap="1">
      Source
      <Flex onClick={e => e.stopPropagation()}>
        <DropdownMenu.Root modal={false}>
          <DropdownMenu.Trigger>
            <IconButton
              size="1"
              variant="ghost"
              color={isFiltered ? undefined : 'gray'}
              aria-label="Filter source results"
            >
              <LuFilter />
            </IconButton>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content variant="soft">
            {SOURCE_OPTIONS.map(option => {
              const isChecked = selectedScopes.includes(option.value)
              const isLastChecked = isChecked && selectedScopes.length === 1

              return (
                <DropdownMenu.Item
                  key={option.value}
                  disabled={isLastChecked}
                  onSelect={e => {
                    e.preventDefault()
                    handleToggle(option.value)
                  }}
                  asChild
                >
                  <Text size="2" as="div" className={styles.optionLabel}>
                    <Flex align="center" gap="2">
                      <Checkbox checked={isChecked} disabled={isLastChecked} />
                      {option.label}
                      {option.tooltip && (
                        <Tooltip content={option.tooltip} avoidCollisions>
                          <a
                            href={MDS_SUPPORT_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Info about MultiDISCO Search"
                            onClick={e => e.stopPropagation()}
                            className={styles.infoLink}
                          >
                            <RxInfoCircled />
                          </a>
                        </Tooltip>
                      )}
                    </Flex>
                  </Text>
                </DropdownMenu.Item>
              )
            })}
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </Flex>
    </Flex>
  )
}
